/**
 * Diálogo de carga levantada: material, peso, dimensão, frequência, distância e método. Grava na hora.
 */
import React, { useState } from 'react';
import { Grid, TextField } from '@mui/material';
import { ergonomicWorkstationService } from '../../../../../services/ergonomicWorkstationService';
import type { ErgonomicLoad } from '../../../../../types/ergonomicWorkstation';
import { dialogFieldSx, parseWeightKg } from './dialogHelpers';
import { ItemDialog } from './ItemDialog';
import { useItemSubmit } from './useItemSubmit';

type LoadField = 'component' | 'weightKg' | 'dimension' | 'frequency' | 'distance' | 'method';

const SERVER_LABELS: ReadonlyArray<[string, LoadField]> = [
  ['Componente', 'component'],
  ['Peso', 'weightKg'],
  ['Dimensão', 'dimension'],
  ['Frequência', 'frequency'],
  ['Distância', 'distance'],
  ['Método', 'method'],
];

interface LoadDialogProps {
  workstationId: number;
  item: ErgonomicLoad | null;
  onSaved: (message: string) => void;
  onClose: () => void;
}

const blankToNull = (value: string): string | null => (value.trim() === '' ? null : value.trim());

export const LoadDialog: React.FC<LoadDialogProps> = ({ workstationId, item, onSaved, onClose }) => {
  const [component, setComponent] = useState(item?.component ?? '');
  const [weight, setWeight] = useState(item?.weightKg == null ? '' : String(item.weightKg));
  const [dimension, setDimension] = useState(item?.dimension ?? '');
  const [frequency, setFrequency] = useState(item?.frequency ?? '');
  const [distance, setDistance] = useState(item?.distance ?? '');
  const [method, setMethod] = useState(item?.method ?? '');
  const { submitting, error, errors, setErrors, run } = useItemSubmit<LoadField>(
    SERVER_LABELS,
    'Não foi possível guardar a carga. Tente de novo em alguns minutos.'
  );

  const submit = async () => {
    const parsedWeight = parseWeightKg(weight);
    const found: Partial<Record<LoadField, string>> = {};
    if (component.trim() === '') found.component = 'Diga qual material ou componente é levantado.';
    if (parsedWeight.error) found.weightKg = parsedWeight.error;
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    const saved = await run(async () => {
      const payload = {
        component: component.trim(),
        weightKg: parsedWeight.value,
        dimension: blankToNull(dimension),
        frequency: blankToNull(frequency),
        distance: blankToNull(distance),
        method: blankToNull(method),
      };
      if (item) await ergonomicWorkstationService.updateLoad(workstationId, item.id, payload);
      else await ergonomicWorkstationService.createLoad(workstationId, payload);
    });
    if (saved) onSaved(item ? 'Carga atualizada.' : 'Carga guardada.');
  };

  return (
    <ItemDialog
      title={item ? 'Editar carga' : 'Nova carga'}
      submitLabel="Guardar carga"
      submitting={submitting}
      error={error}
      onSubmit={submit}
      onClose={onClose}
    >
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <TextField
            fullWidth
            required
            label="Material ou componente"
            value={component}
            onChange={(event) => setComponent(event.target.value)}
            error={Boolean(errors.component)}
            helperText={errors.component || 'Ex.: Tesoura da suspensão'}
            disabled={submitting}
            sx={dialogFieldSx}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth
            label="Peso (kg)"
            value={weight}
            onChange={(event) => setWeight(event.target.value)}
            error={Boolean(errors.weightKg)}
            helperText={errors.weightKg || 'Ex.: 28'}
            disabled={submitting}
            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
            sx={dialogFieldSx}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth
            label="Dimensão"
            value={dimension}
            onChange={(event) => setDimension(event.target.value)}
            error={Boolean(errors.dimension)}
            helperText={errors.dimension || 'Ex.: 45 × 45 cm'}
            disabled={submitting}
            sx={dialogFieldSx}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth
            label="Frequência"
            value={frequency}
            onChange={(event) => setFrequency(event.target.value)}
            error={Boolean(errors.frequency)}
            helperText={errors.frequency || 'Ex.: Uma a cada montagem'}
            disabled={submitting}
            sx={dialogFieldSx}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth
            label="Distância"
            value={distance}
            onChange={(event) => setDistance(event.target.value)}
            error={Boolean(errors.distance)}
            helperText={errors.distance || 'Ex.: 1 metro'}
            disabled={submitting}
            sx={dialogFieldSx}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Método utilizado"
            value={method}
            onChange={(event) => setMethod(event.target.value)}
            error={Boolean(errors.method)}
            helperText={errors.method || 'Ex.: Talha de elevação ou suspensão de material'}
            disabled={submitting}
            sx={dialogFieldSx}
          />
        </Grid>
      </Grid>
    </ItemDialog>
  );
};
